import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { apiErrorMessage } from "@/lib/engine";
import { ENGINE_LANGS, setStoredLang } from "@/i18n/languages";
import { SHELL_NS, hasCatalogs, loadCatalogs } from "@/i18n/lazyCatalogs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// persist="account": PUT /api/users/me/language (fija language_chosen_at). persist="local": qc_lang, para quien
// no tiene sesion. Las compuertas no se miran aqui: decide quien lo monta.
// namespaces por defecto SHELL_NS: garantiza que el menu compartido (AppLayout/shell) este listo antes de cambiar.
export default function LanguageSwitch({
    persist = "account",
    namespaces = SHELL_NS,
    langs = ENGINE_LANGS,
    testid = "language-switch",
    className = "h-9 text-[13px]",
    onChanged,
}) {
    const { t, i18n } = useTranslation("common");
    const updateUser = useAuth()?.updateUser;
    const [busy, setBusy] = useState(false);

    const change = async (language) => {
        const previous = i18n.language;
        if (language === previous || !langs.includes(language)) return;
        setBusy(true);
        // Los catalogos en el idioma nuevo, antes de cambiar: ni claves ni textos a medias.
        if (!hasCatalogs(namespaces, language)) {
            try {
                await loadCatalogs(namespaces, language);
            } catch {
                setBusy(false);
                toast.error(t("language.error"));
                return;
            }
        }
        await i18n.changeLanguage(language);
        if (persist === "local") {
            // Solo falla con un idioma que no admite qc_lang (fuera de ENGINE_LANGS): se deshace el cambio.
            if (setStoredLang(language)) {
                onChanged?.(language);
            } else {
                await i18n.changeLanguage(previous);
                toast.error(t("language.error"));
            }
            setBusy(false);
            return;
        }
        try {
            const r = await api.put("/api/users/me/language", { language });
            updateUser?.(r.data);
            onChanged?.(language);
        } catch (e) {
            await i18n.changeLanguage(previous);
            toast.error(apiErrorMessage(e, t("language.error")));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Select value={i18n.language} onValueChange={change} disabled={busy}>
            <SelectTrigger className={className} data-testid={testid} aria-label={t("language.label")}>
                <SelectValue />
            </SelectTrigger>
            <SelectContent>
                {langs.map((l) => (
                    <SelectItem key={l} value={l} data-testid={`${testid}-${l}`}>{t(`language.${l}`)}</SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}
